import { LinkedinTypes } from 'enums';
import { ScoreProfiles } from 'features/augmented-writing/augmented-writing.types';
import { AutoReply, QualificationPromptPlaceholders } from 'features/co-pilot/campaign-builder/campaign-builder.types';
import { OptionType } from 'features/ui/base-components/pai-auto-complete';
import { Edge, Node, Viewport } from 'reactflow';

export interface CampaignCommunicationSettings {
  language?: 'en' | 'de' | 'auto';
  secondaryLanguage?: 'de' | 'en' | 'none';
  formalSpeech?: boolean;
  includeEmoji?: boolean;
  approveMessages?: boolean;
  swissS?: boolean;
}

export interface Funnel {
  _id: string;
  checkConnectionCount: number;
  checkConnectionCountDate: string;
  blacklists?: string[];
  connectionsCountInitDate: string;
  counterInitDate: string;
  createDate: string;
  creator: string;
  currentStepIndex: number;
  duplicateContactsLength: number;
  filterBySex: FilterBySex;
  funnelContactsLength: number;
  funnelFunctionsLibrary: string;
  isCompleted: boolean;
  lastStatusChange: string;
  linkedinAccount: string | null;
  name: string;
  notifiedTokenExhaustion: boolean;
  offer?: { [key: string]: any };
  profileAnalysisCount: number;
  reactFlow?: FunnelFunctionFlow;
  searchedToday: boolean;
  searches?: Search[];
  status: FunnelStatuses;
  /**
   * Cooldown in days before someone already addressed in another campaign may be
   * contacted again. Undefined / 0 = blocked indefinitely, which is the platform default.
   */
  recontactCooldownDays?: number;
  steps: FunnelStep[];
  toBeApprovedMessages: number;
  toBeApprovedContacts?: number;
  todayGoalsApproximation: any;
  todaysContactOutcomes: Record<string, any>;
  tokenCount: number;
  type: FunnelTypes;
  updatedAt: string;
  workedLastAt: string;
  workedLastFor: number;
  workedLastOn: number;
  workedToday: boolean;
  yesterdayGoalsApproximation: any;
  settings?: CampaignCommunicationSettings;
  judgingSettings?: JudgingSettings;
  template?: string;
  autoReply: AutoReply;
  generateMessagesNow?: boolean;
  lastTestedAt?: string;
  lastTestedSignature?: string;
}

export interface FunnelFunctionFlow {
  nodes: Node[];
  edges: Edge[];
  viewport: Viewport;
}

export interface FunnelContact {
  _id?: string;
  contactId?: string;
  linkedInURL: string;
  recievedMessages: FunnelMessage[];
  sentMessages: FunnelMessage[];
  stepIndex: number;
  isBlacklisted: boolean;
  blacklistId?: string;
  blacklistName?: string;
  isFinished: boolean;
  isSuspended: boolean;
  testContact: boolean;
  globalContact?: string;
  profileIds?: { linkedin?: string; vanity?: string };
  lang?: string;
  waitTime?: string;
  responseSentimment?: string;
  firstName?: string;
  lastName?: string;
  currentCompanies?: { companyName: string; companyUrn: number }[] | string[];
  fullName?: string;
  email?: string;
  occupation?: string;
  phoneNumber?: string;
  shortSummary?: string;
  aiJudgement?: string;
  aiJudgementExplanation?: string;
  location?: string;
  assessment?: string;
  percentiles?: ScoreProfiles;
  isPresentInFunnels?: {
    accountEmail?: string;
    accountFirstName?: string;
    accountLastName?: string;
    linkedinAccountId?: string;
    funnelName: string;
    lastSentMessageDate: Date | null;
  }[];
  searchId?: string;
  searchName?: string;
  fileName?: string;
  isActive?: boolean;
  isDeleted?: boolean;
  /** Held back by the double-contact guard: already approached from another campaign. */
  contactedElsewhere?: boolean;
  contactedElsewhereAt?: string | Date;
  contactedElsewhereCampaignName?: string;
  contactedElsewhereReason?: string;
  preprocessedProfileAnalysis?: boolean;
  customField?: string;
  isReviewed?: boolean;
  stepsTaken?: number[];
  stepTimes?: (number | null)[];
  oldStepTimes?: number[];
  toBeSentMessages?: (string | null)[];
  updateInThread?: boolean;
  bcRequestId?: string | null;
  isJudged?: boolean;
  autoReplyFinished?: boolean;
  threadIds?: {
    linkedin: string | null;
  };
  profile?: {
    authenticity: number;
    jointPosAchieve: number;
    jointPosAffil: number;
    jointPosPower: number;
    regFocus: number;
  };
  profileDegree?: number;
  sex?: string;
  invitationId?: string;
  checkEndTime?: string;
  isCompanyBlacklisted?: boolean;
  suspensionReason?: string;
}

export interface JudgingSettings {
  selectedOptions: string[];
  rejudgeExistingContacts: boolean;
  lastPrompt?: string;
  promptPlaceholders?: QualificationPromptPlaceholders;
  enrichFullFunnel?: boolean;
  updatedAt?: string;
  isJudging?: boolean;
}

interface FunnelMessage {
  sent: boolean;
  text: string;
  textDate: string;
  match: number;
  optimizedText: string;
  persuasionStrategies: string;
}

export interface MessageToApprove {
  profileId: string;
  linkedInURL?: string;
  stepIndex: number;
  text: string;
  subject?: string;
  approved?: boolean;
  receiver?: MessageReceiver;
}

/** Kontakt, der vor der ersten Ansprache auf Freigabe wartet (Kampagneneinstellung `approveContacts`). */
export interface ContactToApprove {
  campaignContactId: string;
  contactId: string;
  name: string;
  headline?: string;
  occupation?: string;
  company?: string;
  location?: string;
  linkedInURL?: string;
  profileId: string;
  profilePictureUrl?: string;
  judgingGrade?: string;
  judgingScore?: number;
  judgingExplanation?: string;
  searchName?: string;
}

export interface MessageSender {
  linkedinAccount?: {
    userEmail: string;
    userFirstName: string;
    userLastName: string;
    liProductType?: LinkedinTypes;
    _id: string;
  };
  funnel?: {
    name: string;
    _id: string;
  };
}

export interface MessageReceiver {
  linkedInURL: string;
  profileId: string;
  name: string;
  position: string;
  company: string;
}

export interface FunnelStep {
  index: number;
  nextStep?: number;
  nextFalseStep?: number;
  functionName:
    | FunnelFunctions
    | 'send_connection_request'
    | 'send_message'
    | 'send_inmail'
    | 'send_email'
    | 'visit_profile'
    | 'check_reply'
    | 'wait';
  waitTillNext?: number;
  waitDays?: number;
  prompt?: string;
  enableSignature?: boolean;
}

export interface FunnelFunction {
  functionName: FunnelFunctions;
  prompt: string;
}

export interface FunnelFunctionLibrary {
  _id: string;
  name: string;
  functions: FunnelFunction[];
}

export type RecruiterLeadPayload = {
  titles: string[];
  general: string[];
  topics: string[];
  profileLangs: string[];
  industries: string[];
  geoUrns: string[];
};

export interface LinkedInLeadSearchResult {
  URL?: string;
  payload?: RecruiterLeadPayload;
  keywords?: { titles?: string[]; general?: string[]; topics?: string[]; profileLangs?: string[]; industries?: string[]; searchString?: string };
  linkedinFreq?: number;
  searchName?: string;
  index?: number;
}

export type RecruiterTalentPayload = {
  geoUrns: { entityUrn: string; text: string }[] | string[];
  titles: string[];
  industries: string[];
  keywords: string[];
  network: string[];
  yearsOfExperience: [number, number];
  skills?: string[];
};

export interface RecruiterConstraint {
  entityUrn: string;
  text: string;
  exclude: boolean;
}

export interface RefinedRecruiterSearchPayload {
  titles?: RecruiterConstraint[];
  skills?: RecruiterConstraint[];
  geoUrns?: RecruiterConstraint[];
  positions?: RecruiterConstraint[];
  companies?: RecruiterConstraint[];
  industries?: RecruiterConstraint[];
  zip?: RecruiterConstraint[];
  area?: string;
  keywords?: string;
  yearsOfExperience?: [number, number];
}

interface CurrentCompany {
  title: string;
  companyName: string;
  companyUrn: string;
}

export interface RecruiterContact {
  url: string;
  name: string;
  currentCompanies: CurrentCompany[];
  selected?: boolean;
}

export interface Constraint {
  query: string;
  options: OptionType[];
  selected: OptionType[];
  titleType?: string;
  loading: boolean;
  isEmpty?: boolean;
}

export interface RegularConstraint {
  query: string;
  options: string[][];
  isEmpty: boolean;
  selected: string[][];
  loading: boolean;
}

export enum RecruiterSearchTypes {
  OCCUPATION = 'occupation',
  SKILL = 'skill',
  INDUSTRY = 'industry',
  COMPANY = 'company',
  LOCATION = 'geo',
  ZIP = 'zip',
  AREA = 'area',
  KEYWORDS = 'keywords',
}

export enum TitleTypes {
  OCCUPATION = 'occupation',
  TITLE = 'title',
}

export const RefinedRecruiterSearchPayloadFields = {
  [RecruiterSearchTypes.OCCUPATION]: 'titles',
  [RecruiterSearchTypes.SKILL]: 'skills',
  [RecruiterSearchTypes.INDUSTRY]: 'industries',
  [RecruiterSearchTypes.COMPANY]: 'companies',
  [RecruiterSearchTypes.LOCATION]: 'geoUrns',
  [RecruiterSearchTypes.ZIP]: 'postalCodes',
};

export type RecruiterSearch = {
  occupation: Constraint;
  skill: Constraint;
  industry: Constraint;
  company: Constraint;
  geo: Constraint;
  zip: Constraint;
  area: string;
  keywords: string;
  yearsOfExperience: [number, number];
};

export type RecruiterLocationSearch = {
  geo: Constraint;
  zip: Constraint;
  area: string;
};

export type RecruiterSearchKeys = Exclude<keyof RecruiterSearch, 'area' | 'keywords' | 'yearsOfExperience'>;

export interface LinkedInTalentSearchResult {
  URL?: string;
  payload?: RecruiterTalentPayload;
  searchName?: string;
  index?: number;
  linkedinFreq: number;
}

export interface LinkedInCrawlerContact {
  _id?: string;
  url?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  currentCompanies?: string[];
  company?: string;
  email?: string;
  occupation?: string;
  phoneNumber?: string;
  fileName?: string;
}

export interface AddContact {
  linkedinUrl: string;
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  phoneNumber?: string;
}

export type CrawlerOptions = {
  includeReactions?: boolean;
  includeComments?: boolean;
  includeAuthor?: boolean;
  includedProfileDegrees?: number[];
};

export interface Search {
  _id: string;
  name?: string;
  constraints: any;
  keywords: string[];
  locations: string[];
  createDate: string;
  liProductType: LinkedinTypes;
  linkedinAccount: string;
  position: number;
  url: string;
  status: ContactsSearchStatuses;
  oldStatus: ContactsSearchStatuses;
  state?: {
    crawled?: number;
    total?: number;
    pageNumber?: number;
    totalPages?: number;
    /** Profiles LinkedIn returned without a name and that therefore could not be added. */
    withheld?: number;
  };
  /**
   * Number of contacts that were actually saved to the funnel.
   * Added 2024-07-09 – replaces the former state.crawled metric.
   */
  saved?: number;
  error?: string;
  nextRetryAt?: string;
  crawlerOptions?: CrawlerOptions;
  type: BackgroundSearchTypes;
}

export interface FileUpload {
  name: string;
  type: string;
  contactCount: number;
}

export interface PerplexityTalentResponse {
  jobtitle?: string;
  jobadtext?: string;
}

export interface PerplexityLeadResponse {
  problems?: string;
  features?: string;
  benefits?: string;
  impact?: string;
}

export interface FunnelPayload {
  name: string;
  type: FunnelTypes;
  funnelContacts?: LinkedInCrawlerContact[];
  offer?: {
    jobTitle?: string;
    jobOffer?: string;
    problems?: string;
    features?: string;
    benefits?: string;
    impact?: string;
  };
  activeSourcingID?: string;
  socialSellingID?: string;
}

export interface CampaignFunction {
  functionName: string;
  prompt: string;
  disabled?: boolean;
  disabledReason?: boolean;
}

const enum BackgroundSearchTypes {
  PEOPLE_SEARCH = 'peopleSearch',
  POSTING_SEARCH = 'postingSearch',
  JOB_SEARCH = 'jobSearch',
  PROJECT = 'project',
  COURSE_SEARCH = 'courseSearch',
  ACTIVITY_SEARCH = 'activitySearch',
  GROUP_MEMBERS_SEARCH = 'groupMemberSearch',
  EVENT_ATTENDEES_SEARCH = 'eventAttendeesSearch',
  SAVED_SEARCH = 'savedSearch',
}

export enum FunnelDays {
  MONDAY = 'monday',
  TUESDAY = 'tuesday',
  WEDNESDAY = 'wednesday',
  THURSDAY = 'thursday',
  FRIDAY = 'friday',
  SATURDAY = 'saturday',
  SUNDAY = 'sunday',
}

export enum FunnelFunctions {
  SEND_CONNECTION_REQUEST = 'sendConnectionRequest',
  SEND_FOLLOW_UP = 'sendFollowUp',
  SEND_MAIL = 'sendMail',
  SEND_MAIL_WITH_CONTEXT = 'sendMailWithContext',
  SEND_IN_MAIL = 'sendInMail',
  SEND_IN_MAIL_WITH_CONTEXT = 'sendInMailWithContext',
  SEND_EMAIL = 'sendEmail',
  SEND_FOLLOW_UP_EMAIL = 'sendFollowUpEmail',
  SEND_EMAIL_WITH_CONTEXT = 'sendEmailWithContext',
  CHECK_REPLY = 'checkReply',
  CHECK_IF_CONNECTED = 'checkIfConnected',
  CHECK_IF_CONNECTED_UNTIL = 'checkIfConnectedUntil',
  WAIT_STEP = 'waitStep',
  CHECK_IF_GENERATED_EMAIL_ADDRESS = 'checkIfGeneratedEmailAddress',
  /**
   * Detect whether the last messages with the contact should be answered in a polite (formal) or casual (informal) tone.
   * The backend analyses the last 3-5 messages automatically and returns { isPolite: boolean } in the test run.
   */
  CHECK_IS_POLITE = 'checkIsPolite',
  WITHDRAW_CONNECTION_REQUEST = 'withdrawConnectionRequest',
  SEND_CALL_TRANSCRIPT = 'sendCallTranscript',
}

export enum FunnelTypes {
  ACTIVE_SOURCING_CO_PILOT = 'active_sourcing_co_pilot',
  SOCIAL_SELLING_CO_PILOT = 'social_selling_co_pilot',
}

export enum FunnelStatuses {
  CREATED = 'created',
  STARTED = 'started',
  FINISHED = 'finished',
  SUSPENDED = 'suspended',
  PAUSED = 'paused',
  ACCOUNT_NOT_FOUND = 'account-not-found',
  LINKEDIN_ACCOUNT_PROBLEM = 'linkedin-account-problem',
  CO_PILOT_LICENSE_EXPIRED = 'co-pilot-license-expired',
  CONTACTS_ALMOST_EXHAUSTED = 'contacts-almost-exhausted',
  ARCHIVED = 'archived',
}

export enum LinkedinCrawlerSearchTypes {
  PEOPLE_SEARCH = 'peopleSearch',
  POSTING_SEARCH = 'postingSearch',
  URL_FINDER = 'urlFinder',
}

export enum ContactsSearchStatuses {
  PENDING = 'pending',
  RUNNING = 'running',
  FINISHED = 'finished',
  QUOTA_EXCEEDED = 'quota-exceeded',
  RECRUITER_PARALLEL_LOGIN = 'recruiter-parallel-login',
  ACCOUNT_NOT_FOUND = 'account-not-found',
  LINKEDIN_ACCOUNT_PROBLEM = 'linkedin-account-problem',
  SYNCHRONIZING_LINKEDIN_ACCOUNT = 'synchronizing-linkedin-account',
}

export enum FilterBySex {
  KEEP_BOTH = 'keepBoth',
  KEEP_FEMALE = 'keepFemale',
  KEEP_MALE = 'keepMale',
}

export enum NodeVariants {
  FUNCTION_NODE = 'functionNode',
  CONDITIONAL_NODE = 'conditionalNode',
  CONDITION_WITH_DURATION_NODE = 'conditionWithDurationNode',
  WAIT_NODE = 'waitNode',
  UTILITY_NODE = 'utilityNode',
}

export enum FileUploadMatchColumns {
  EMPTY = '',
  FIRST_NAME = 'firstName',
  LAST_NAME = 'lastName',
  FULL_NAME = 'fullName',
  LINKEDIN_URL = 'linkedinUrl',
  COMPANY = 'company',
  EMAIL = 'email',
  COUNTRY = 'country',
  ROLE = 'role',
  POSITION = 'position',
  CUSTOM_FIELD = 'customField',
  PHONE_NUMBER = 'phoneNumber',
}

export enum CampaignFilterTypes {
  FUNNEL_NAME = 'funnelName',
  FUNNEL_STATUS = 'funnelStatus',
  LINKEDIN_ACCOUNT = 'liAccount',
}

export const CanSendViaSalesNav = [FunnelFunctions.SEND_MAIL, FunnelFunctions.SEND_MAIL_WITH_CONTEXT, FunnelFunctions.SEND_FOLLOW_UP];
